import { localStorageGet } from '@core/cookie';
import {
    workspaceConferenceAgendasPath,
    workspaceConferenceEvaluationPath,
    workspaceConferenceGuestJourneysPath,
    workspaceConferenceGuestsPath,
    workspaceConferenceInfoPath,
    workspaceConferenceLaborfeePath,
    workspaceConferenceLivesPath,
    workspaceConferenceOrganizationsPath,
    workspaceConferenceSignupPath,
    workspaceConferenceSiteModulesPath,
} from '../conferenceWorkspacePaths';

/**
 * 从 localStorage 中读取角色，判断会议项目工作空间相关菜单的可见性。
 * 与 ConferenceList 原内联按钮上的角色判定保持一致。
 *
 * @returns {{ canShowLaborFee: boolean, canEvalExpert: boolean, canManageGuestJourney: boolean }}
 */
export function readConferenceWorkspacePermissions() {
    let roleList = [];
    try {
        const raw = localStorageGet('roleList');
        roleList = raw ? JSON.parse(raw) || [] : [];
    } catch {
        roleList = [];
    }

    const canShowLaborFee = roleList.some(item => item.name === '劳务费录入人员' || item.name === '劳务费管理员');
    const canEvalExpert = roleList.some(item => item.name === '评价专家参会情况');
    const canManageGuestJourney = roleList.some(item => item.name === '嘉宾行程管理');

    return { canShowLaborFee, canEvalExpert, canManageGuestJourney };
}

/**
 * ConferenceSider / 移动抽屉的菜单条目构造器。
 * 对应 ConferenceList 操作列：内联为网站模块 / 日程 / 嘉宾 / 直播 / 项目信息 / 报名签到；
 * 「更多」菜单见 buildConferenceListMoreMenuItems（嘉宾行程 / 专家价值 / 专家劳务按权限，机构权益及维护项见该函数）。
 *
 * 「项目信息」：`/conf/conferences/:conferenceId`，`conferenceId === 'new'` 时为「新增项目」，仅该项可用。
 *
 * @param {string} conferenceId
 * @param {{
 *   isCreateMode?: boolean,
 *   canShowLaborFee?: boolean,
 *   canEvalExpert?: boolean,
 *   canManageGuestJourney?: boolean,
 * }} [options]
 * @returns {{ key: string, label: string, disabled?: boolean, icon: string }[]}
 */
export function buildConferenceWorkspaceMenuEntries(conferenceId, options = {}) {
    const { isCreateMode = false, canShowLaborFee = false, canEvalExpert = false, canManageGuestJourney = false } = options;

    if (isCreateMode) {
        // 新增项目仅「项目信息」可用；实际路径为 `/conf/conferences/new` 本身（由 Layout 的 index 路由渲染表单）
        const entries = [
            { key: `/conf/conferences/${conferenceId}`, label: '项目信息', icon: 'bi bi-info-circle' },
            { key: workspaceConferenceSiteModulesPath(conferenceId), label: '会议官网', disabled: true, icon: 'bi bi-globe' },
            { key: workspaceConferenceAgendasPath(conferenceId), label: '会议日程', disabled: true, icon: 'bi bi-calendar-week' },
            { key: workspaceConferenceGuestsPath(conferenceId), label: '会议嘉宾', disabled: true, icon: 'bi bi-people' },
        ];

        if (canManageGuestJourney) {
            entries.push({ key: workspaceConferenceGuestJourneysPath(conferenceId), label: '嘉宾行程', disabled: true, icon: 'bi bi-calendar-event' });
        }

        entries.push(
            { key: workspaceConferenceLivesPath(conferenceId), label: '会议直播', disabled: true, icon: 'bi bi-camera-video' },
            { key: workspaceConferenceSignupPath(conferenceId), label: '报名签到', disabled: true, icon: 'bi bi-clipboard-check' },
            { key: workspaceConferenceOrganizationsPath(conferenceId), label: '机构权益', disabled: true, icon: 'bi bi-building' },
        );

        if (canEvalExpert) {
            entries.push({ key: workspaceConferenceEvaluationPath(conferenceId), label: '专家价值', disabled: true, icon: 'bi bi-award' });
        }

        if (canShowLaborFee) {
            entries.push({ key: workspaceConferenceLaborfeePath(conferenceId), label: '专家劳务', disabled: true, icon: 'bi bi-cash' });
        }

        return entries;
    }

    const entries = [
        { key: workspaceConferenceInfoPath(conferenceId), label: '项目信息', icon: 'bi bi-info-circle' },
        { key: workspaceConferenceSiteModulesPath(conferenceId), label: '会议官网', icon: 'bi bi-globe' },
        { key: workspaceConferenceAgendasPath(conferenceId), label: '会议日程', icon: 'bi bi-calendar-week' },
        { key: workspaceConferenceGuestsPath(conferenceId), label: '会议嘉宾', icon: 'bi bi-people' },
    ];

    if (canManageGuestJourney) {
        entries.push({ key: workspaceConferenceGuestJourneysPath(conferenceId), label: '嘉宾行程', icon: 'bi bi-calendar-event' });
    }

    entries.push(
        { key: workspaceConferenceLivesPath(conferenceId), label: '会议直播', icon: 'bi bi-camera-video' },
        { key: workspaceConferenceSignupPath(conferenceId), label: '报名签到', icon: 'bi bi-clipboard-check' },
        { key: workspaceConferenceOrganizationsPath(conferenceId), label: '机构权益', icon: 'bi bi-building' },
    );

    if (canEvalExpert) {
        entries.push({ key: workspaceConferenceEvaluationPath(conferenceId), label: '专家价值', icon: 'bi bi-award' });
    }

    if (canShowLaborFee) {
        entries.push({ key: workspaceConferenceLaborfeePath(conferenceId), label: '专家劳务', icon: 'bi bi-cash' });
    }

    return entries;
}

/**
 * 会议项目列表「更多」下拉菜单项（权限与工作空间侧栏一致）。
 *
 * @param {object} record 列表行数据
 * @param {{ canShowLaborFee: boolean, canEvalExpert: boolean, canManageGuestJourney: boolean }} permissions
 * @param {{
 *   gotoConferenceManagement: (projectId: string|number, moduleName: string, state?: object) => void,
 *   markAsTesting: (record: object) => void,
 *   delConference: (id: string|number, name: string) => void,
 * }} handlers
 * @returns {import('antd').MenuProps['items']}
 */
export function buildConferenceListMoreMenuItems(record, permissions, handlers) {
    const { canShowLaborFee, canEvalExpert, canManageGuestJourney } = permissions;
    const { gotoConferenceManagement, markAsTesting, delConference } = handlers;
    const conferenceState = { name: record.name, slug: record.slug };

    const items = [];

    if (canManageGuestJourney) {
        items.push({
            label: '嘉宾行程',
            key: 'guestJourneys',
            onClick: () => gotoConferenceManagement(record.id, 'guests/journeys', conferenceState),
        });
    }

    if (canEvalExpert) {
        items.push({
            label: '专家价值',
            key: 'evaluation',
            onClick: () => gotoConferenceManagement(record.id, 'evaluation', conferenceState),
        });
    }

    if (canShowLaborFee) {
        items.push({
            label: '专家劳务',
            key: 'laborfee',
            onClick: () => gotoConferenceManagement(record.id, 'laborfee', conferenceState),
        });
    }

    items.push({
        label: '机构权益',
        key: 'organizations',
        onClick: () => gotoConferenceManagement(record.id, 'organizations', conferenceState),
    });

    const maintenanceItems = [];

    maintenanceItems.push(
        {
            label: !!record.isTesting ? '标为正式会议' : '标为测试会议',
            key: 'markAsTesting',
            onClick: () => markAsTesting(record),
        },
        {
            label: '删除会议项目',
            key: 'delete',
            onClick: () => delConference(record.id, record.name),
            danger: true,
        },
    );

    if (maintenanceItems.length > 0) {
        if (items.length > 0) {
            items.push({ type: 'divider' });
        }
        items.push(...maintenanceItems);
    }

    return items;
}
